// src/context/AmbitoContext.js
//
// Estado GLOBAL del "ámbito administrativo" del usuario — qué conjunto
// de datos del tenant está mirando/gestionando ahora mismo.
//
// Ejemplos:
//   - "Red (Fenagro / cursos globales del tenant)"  → institucion_id = NULL
//   - "Instituto Test"                              → institucion_id = XXX
//
// Vive en un contexto global (no en el componente del panel admin)
// porque el selector debe ser accesible desde el menú del perfil en
// el Header, no solo desde una pantalla específica. Así, independiente
// de dónde navegue el usuario, puede cambiar su ámbito activo.
//
// Reglas:
//   - Si el usuario tiene SuperAdmin cross-ecosistema (claim "role" del
//     JWT de Ecolab) o rol Administrador local de EducaT, se le muestra
//     la opción "Red" + TODAS las instituciones del tenant.
//   - Si no, se le muestran solo las instituciones a las que está
//     vinculado por usuario_institucion.
//   - El ámbito activo se persiste en localStorage para no perderse
//     entre refresh / navegación.
//
// Al cambiar el ámbito, los componentes consumidores (AdminDashboard,
// etc.) reaccionan recargando sus datos filtrados.

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from './AppContext';
import { meService, institucionService } from '../services/api';

const STORAGE_KEY = 'educat.ambitoId';
export const AMBITO_RED = '__RED__';   // id virtual del ámbito "red / globales del tenant"

const AmbitoContext = createContext(null);

export function AmbitoProvider({ children }) {
  const { user, isAuthenticated } = useAuth();

  // Admin del tenant = Administrador local de EducaT OR SuperAdmin cross-ecosistema del JWT.
  // El segundo criterio existe para evitar el race condition del GET /me:
  // ecolabRoles se decodifica del JWT instantáneamente al login.
  const esAdminTenant = useMemo(() => (
    user?.role === 'admin' ||
    (Array.isArray(user?.ecolabRoles) && user.ecolabRoles.includes('SuperAdmin'))
  ), [user]);

  const [opciones, setOpciones]     = useState([]);  // [{ id, nombre }]
  const [ambitoId, setAmbitoIdRaw]  = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [cargando, setCargando]     = useState(false);

  // Cargar opciones disponibles cada vez que cambia el usuario o su rol.
  useEffect(() => {
    if (!isAuthenticated) {
      setOpciones([]);
      setAmbitoIdRaw(null);
      return;
    }
    let cancelado = false;
    (async () => {
      setCargando(true);
      try {
        let instituciones = [];
        if (esAdminTenant) {
          instituciones = await institucionService.getAll({ soloActivas: true }).catch(() => []);
        } else {
          instituciones = await meService.getMisInstituciones().catch(() => []);
        }
        if (cancelado) return;

        const ops = (instituciones || []).map((i) => ({
          id: i.id,
          nombre: i.name || i.nombre,
          esRed: false,
        }));
        // Opción "Red" al inicio solo para admin del tenant.
        if (esAdminTenant) {
          ops.unshift({ id: AMBITO_RED, nombre: 'Red (cursos globales del tenant)', esRed: true });
        }
        setOpciones(ops);

        // Validar / fijar ámbito activo
        const guardado = localStorage.getItem(STORAGE_KEY);
        const sigueExistiendo = guardado && ops.some((o) => o.id === guardado);
        if (sigueExistiendo) {
          setAmbitoIdRaw(guardado);
        } else if (ops.length > 0) {
          // Para admins del tenant, por defecto arrancar en la Red.
          const nuevo = esAdminTenant ? AMBITO_RED : ops[0].id;
          setAmbitoIdRaw(nuevo);
          localStorage.setItem(STORAGE_KEY, nuevo);
        } else {
          setAmbitoIdRaw(null);
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        console.warn('[AmbitoProvider] error cargando opciones:', e.message);
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();
    return () => { cancelado = true; };
  }, [isAuthenticated, esAdminTenant, user?.id]);

  const setAmbitoId = useCallback((nuevo) => {
    setAmbitoIdRaw(nuevo);
    if (nuevo) localStorage.setItem(STORAGE_KEY, nuevo);
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const ambitoActual = useMemo(() => opciones.find((o) => o.id === ambitoId) || null, [opciones, ambitoId]);
  const esRed = ambitoId === AMBITO_RED;

  // Helper para pasar al backend como filtro en cursoService.getAll y similares.
  // null → sin filtro (red/globales).
  const institucionIdActual = esRed ? null : ambitoId;

  const value = useMemo(() => ({
    opciones,
    ambitoId,
    ambitoActual,
    ambitoNombre: ambitoActual?.nombre || '',
    esRed,
    esAdminTenant,
    institucionIdActual,
    setAmbitoId,
    cargando,
  }), [opciones, ambitoId, ambitoActual, esRed, esAdminTenant, institucionIdActual, setAmbitoId, cargando]);

  return <AmbitoContext.Provider value={value}>{children}</AmbitoContext.Provider>;
}

export function useAmbito() {
  const ctx = useContext(AmbitoContext);
  if (!ctx) throw new Error('useAmbito debe usarse dentro de AmbitoProvider');
  return ctx;
}
